import React, { useState } from "react";
import { encryptPassword } from "../../utils/cryptoUtils";
import "./ForgotPasswordPage.style.scss";
import { CommonMethods } from "./../../components/common/CommonMethods";
import { useNavigate } from "react-router-dom";
import nogruntblacklogo from "../../Assets/nogruntblacklogo.svg";
import { useNotification } from "../../hooks";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { getBrandLogo } from "../../utils/userDetailsUtils";

import {
  fetchForgotPasswordToken,
  validateOTP,
  changepasswordFromForgotPassword,
} from "./../../API/UserApi";

const ForgotPasswordPage = (props) => {
  const navigate = useNavigate();
  const notify = useNotification();
  const [account, setAccount] = useState({
    email: "",
    token: "",
    NewPassword: "",
    RENewPassword: "",
  });
  const [isTokenGenerated, setIsTokenGenerated] = useState(false);
  const [isTokenvalidated, setIsTokenvalidated] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const logoToShow = getBrandLogo(nogruntblacklogo);

  const handelAccount = (property, event) => {
    let value = event.target.value;
    if (property === "email") {
      value = value.replace(/[<>"'`\\|(){}[\]]/g, "");
    }
    if (property === "token") {
      value = value.replace(/[^0-9]/g, "");
    }
    setAccount({ ...account, [property]: value });
  };

  const validateEmail = (email) => {
    return email.match(
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
  };

  const validatePassword = (password) => {
    return password.match(
      /^(?!.*\s)(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[~`!@#$%^&*()--+={}[\]|\\:;"'<>,.?/_₹]).{8,16}$/,
    );
  };

  const GetForgotPasswordToken = async () => {
    if (!account.email) {
      notify("Please enter your email address.", "error");
      return;
    }
    if (!validateEmail(account.email)) {
      notify("Please enter a valid email address.", "error");
      return;
    }
    setIsLoading(true);
    await fetchForgotPasswordToken(account.email)
      .then((response) => {
        if (response.status === 200) {
          if (response.data.message === "fail") {
            CommonMethods(navigate);
          } else {
            setIsTokenGenerated(true);
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const CheckOTP = async () => {
    if (!account.token) {
      notify("Please enter the verification code.", "error");
      return;
    }
    setIsLoading(true);
    await validateOTP(account.email, account.token)
      .then((response) => {
        if (response.status === 200) {
          if (response.data.message === "fail") {
            CommonMethods(navigate);
          } else {
            if (response.data === "Token Verified") {
              setIsTokenvalidated(true);
            }
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const changePassword = async () => {
    if (!account.NewPassword || !account.RENewPassword) {
      notify("Please fill in all password fields.", "error");
      return;
    }
    if (account.RENewPassword !== account.NewPassword) {
      notify("Passwords do not match.", "error");
      return;
    }
    if (!validatePassword(account.NewPassword)) {
      notify("Password doesn't meet the requirements.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const encryptedPassword = await encryptPassword(account.NewPassword);
      await changepasswordFromForgotPassword({
        username: account.email,
        NewPassword: encryptedPassword,
        RENewPassword: encryptedPassword,
      })
        .then((response) => {
          if (response.status === 200) {
            if (response.data.message === "fail") {
              CommonMethods(navigate);
            } else {
              navigate("/LoginPage");
            }
          }
        })
        .catch(() => {});
    } catch (error) {
      notify("Password reset failed. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const currentStep = !isTokenGenerated ? 1 : isTokenGenerated && !isTokenvalidated ? 2 : 3;

  const getInputClassName = (field, error) => {
    let base = "w-full px-4 py-3 border-2 rounded-xl outline-none transition-all duration-200";
    if (error) {
      return `${base} border-red-300 bg-red-50`;
    }
    if (focusedField === field) {
      return `${base} border-blue-500 bg-white`;
    }
    return `${base} border-gray-200 bg-gray-50 hover:border-gray-300`;
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 px-4 py-6">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <img src={logoToShow} alt="Logo" className="h-10" />
            <button
              onClick={() => navigate("/LoginPage")}
              className="text-sm text-gray-500 hover:text-blue-600 transition-colors flex items-center gap-2 cursor-pointer"
              style={{ fontFamily: "Urbanist, sans-serif" }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                    step < currentStep
                      ? "bg-green-500 text-white"
                      : step === currentStep
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-400"
                  }`}
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                >
                  {step < currentStep ? <Check className="w-5 h-5" strokeWidth={2.5} /> : step}
                </div>
                {step < 3 && (
                  <div
                    className={`w-12 h-1 rounded-full transition-all duration-300 ${step < currentStep ? "bg-green-500" : "bg-gray-200"}`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step labels */}
          <div
            className="flex items-center justify-between px-2 mb-6 text-xs"
            style={{ fontFamily: "Urbanist, sans-serif" }}
          >
            <span className={currentStep >= 1 ? "text-gray-700 font-medium" : "text-gray-400"}>
              Email
            </span>
            <span className={currentStep >= 2 ? "text-gray-700 font-medium" : "text-gray-400"}>
              Verify
            </span>
            <span className={currentStep >= 3 ? "text-gray-700 font-medium" : "text-gray-400"}>
              Reset
            </span>
          </div>

          {/* Step 1: Email */}
          {!isTokenGenerated && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h1
                  className="text-xl font-bold text-gray-900 mb-2"
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                >
                  Forgot your password?
                </h1>
                <p className="text-gray-500 text-sm" style={{ fontFamily: "Urbanist, sans-serif" }}>
                  Enter your email and we'll send you a verification code.
                </p>
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                >
                  Email address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={account.email}
                  onChange={(e) => handelAccount("email", e)}
                  placeholder="Enter your email"
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  className={getInputClassName(
                    "email",
                    !validateEmail(account.email) && account.email.length > 0,
                  )}
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                />
                {!validateEmail(account.email) && account.email.length > 0 && (
                  <p
                    className="text-red-500 text-xs mt-1"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    Enter a valid email address
                  </p>
                )}
              </div>

              <button
                onClick={GetForgotPasswordToken}
                disabled={!account.email || isLoading}
                className={`w-full py-3.5 font-semibold rounded-xl transition-all duration-200 cursor-pointer ${
                  !account.email || isLoading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
                style={{ fontFamily: "Urbanist, sans-serif" }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin h-5 w-5" />
                    Sending...
                  </span>
                ) : (
                  "Send verification code"
                )}
              </button>
            </div>
          )}

          {/* Step 2: OTP Verification */}
          {isTokenGenerated && !isTokenvalidated && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h1
                  className="text-xl font-bold text-gray-900 mb-2"
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                >
                  Check your email
                </h1>
                <p className="text-gray-500 text-sm" style={{ fontFamily: "Urbanist, sans-serif" }}>
                  We've sent a code to <strong className="text-gray-700">{account.email}</strong>
                </p>
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                >
                  Verification code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={account.token}
                  onChange={(e) => handelAccount("token", e)}
                  placeholder="Enter the code"
                  onFocus={() => setFocusedField("token")}
                  onBlur={() => setFocusedField(null)}
                  className={getInputClassName("token")}
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                />
              </div>

              <button
                onClick={CheckOTP}
                disabled={!account.token || isLoading}
                className={`w-full py-3.5 font-semibold rounded-xl transition-all duration-200 cursor-pointer ${
                  !account.token || isLoading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
                style={{ fontFamily: "Urbanist, sans-serif" }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin h-5 w-5" />
                    Verifying...
                  </span>
                ) : (
                  "Verify code"
                )}
              </button>

              <button
                onClick={GetForgotPasswordToken}
                className="w-full text-center text-sm text-gray-500 hover:text-blue-600 transition-colors cursor-pointer"
                style={{ fontFamily: "Urbanist, sans-serif" }}
              >
                Didn't receive the code? <span className="font-semibold">Resend</span>
              </button>
            </div>
          )}

          {/* Step 3: New Password */}
          {isTokenvalidated && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h1
                  className="text-xl font-bold text-gray-900 mb-2"
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                >
                  Create new password
                </h1>
                <p className="text-gray-500 text-sm" style={{ fontFamily: "Urbanist, sans-serif" }}>
                  Your new password must be different from previous passwords.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-2"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    New password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    required
                    value={account.NewPassword}
                    onChange={(e) => handelAccount("NewPassword", e)}
                    placeholder="Enter new password"
                    onFocus={() => setFocusedField("NewPassword")}
                    onBlur={() => setFocusedField(null)}
                    className={getInputClassName(
                      "NewPassword",
                      !validatePassword(account.NewPassword) && account.NewPassword.length > 0,
                    )}
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  />
                  {!validatePassword(account.NewPassword) && account.NewPassword.length > 0 && (
                    <p
                      className="text-red-500 text-xs mt-1"
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    >
                      Password doesn't meet requirements
                    </p>
                  )}
                  <p
                    className="text-xs text-gray-400 mt-1"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    8-16 characters, uppercase, lowercase, number, special character
                  </p>
                </div>

                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-2"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    Confirm new password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    required
                    value={account.RENewPassword}
                    onChange={(e) => handelAccount("RENewPassword", e)}
                    placeholder="Confirm new password"
                    onFocus={() => setFocusedField("RENewPassword")}
                    onBlur={() => setFocusedField(null)}
                    className={getInputClassName(
                      "RENewPassword",
                      account.NewPassword !== account.RENewPassword &&
                        account.RENewPassword.length > 0,
                    )}
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  />
                  {account.NewPassword !== account.RENewPassword &&
                    account.RENewPassword.length > 0 && (
                      <p
                        className="text-red-500 text-xs mt-1"
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      >
                        Passwords don't match
                      </p>
                    )}
                </div>
              </div>

              <button
                onClick={changePassword}
                disabled={!account.NewPassword || !account.RENewPassword || isLoading}
                className={`w-full py-3.5 font-semibold rounded-xl transition-all duration-200 cursor-pointer ${
                  !account.NewPassword || !account.RENewPassword || isLoading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
                style={{ fontFamily: "Urbanist, sans-serif" }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin h-5 w-5" />
                    Resetting...
                  </span>
                ) : (
                  "Reset password"
                )}
              </button>
            </div>
          )}

          {/* Footer links */}
          <div
            className="flex items-center justify-center gap-4 mt-6 text-sm"
            style={{ fontFamily: "Urbanist, sans-serif" }}
          >
            <button
              onClick={() => navigate("/SignUpPage")}
              className="text-blue-600 hover:text-blue-700 font-medium transition-colors cursor-pointer"
            >
              Create account
            </button>
            <span className="text-gray-300">•</span>
            <button
              onClick={() => navigate("/LoginPage")}
              className="text-blue-600 hover:text-blue-700 font-medium transition-colors cursor-pointer"
            >
              Sign in
            </button>
          </div>
        </div>

        {/* Footer */}
        <div
          className="text-center text-sm text-gray-400 mt-6"
          style={{ fontFamily: "Urbanist, sans-serif" }}
        >
          Need help?{" "}
          <a
            href="https://www.nogrunt.com/contact-us/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline cursor-pointer"
          >
            Contact support
          </a>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
